import { useCallback, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import Cookies from 'js-cookie';

import { loginToTeamLocker, TeamLockerLoginError } from '@api/auth/teamLockerSession';
import { trackHeapEvent, identifyHeapUser, addHeapUserProperties } from '@components/HeapEvents';
import { useDefaultConversionTracker } from '@hooks/useDefaultConversionTracker';
import { useAppContext } from '@context/AppContext';
import { FAN_FAVORITES_UTM_VARIANT_VALUE, GET_STARTED_COOKIE_NAME } from '@constants/appConstants';
import { hasUtmVariantCookie } from '@utils/utmVariantCookie';
import { getLogger } from '@logging/logger';
import { usePostStorefrontPublicApiV1Signup } from '@api/__generated__/accounts_public/signup/signup';
import { parseValidationError } from '@api/errors';
import type { DIYSignupCommand } from '@api/__generated__/accounts_public/accounts.schemas';

interface SubmitSignupParams {
  signupData: DIYSignupCommand;
  // password and variant are read from here (not signupData) because the payload types them as nullable,
  // while authentication and tracking below need definite values. email/firstName/lastName come from signupData.
  password: string;
  variant: string;
  cookieValue?: string;
}

interface UseSignupReturn {
  submitSignup: (params: SubmitSignupParams) => Promise<string | null>;
  isPending: boolean;
  isAuthenticating: boolean;
  authenticate: (firstName: string, lastName: string, email: string, password: string) => Promise<string | null>;
}

export function useSignupSuccess(): UseSignupReturn {
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const navigate = useNavigate();
  const { trackConversion } = useDefaultConversionTracker();
  const { setUserData } = useAppContext();
  const { mutateAsync: signup, isPending } = usePostStorefrontPublicApiV1Signup();

  const authenticate = useCallback(
    async (firstName: string, lastName: string, email: string, password: string): Promise<string | null> => {
      setIsAuthenticating(true);
      let teamLockerError: string | null = null;
      try {
        // The Set-Cookie on the /oauth/token response stores the shared OAuth cookie (OAUTH_TOKEN_NAME)
        await loginToTeamLocker({ email, password });
        setUserData({ email, firstName, lastName });
      } catch (error) {
        getLogger().error('teamlocker login failed after signup', {}, error);
        teamLockerError =
          error instanceof TeamLockerLoginError ? error.message : 'We created your account, but signing you in failed. Please log in to continue.';
      } finally {
        setIsAuthenticating(false);
      }
      return teamLockerError;
    },
    [setUserData],
  );

  const submitSignup = useCallback(
    async ({ signupData, password, variant, cookieValue }: SubmitSignupParams): Promise<string | null> => {
      const { email, firstName, lastName } = signupData;
      try {
        const response = await signup({ data: signupData });

        if (!response?.success || !response?.result?.lockerId) {
          return 'Failed to create store. Please try again.';
        }

        const { lockerId } = response.result;

        identifyHeapUser(email);
        addHeapUserProperties({ firstName, lastName, email, lockerId });

        let teamLockerError: string | null = null;
        if (email && password) {
          teamLockerError = await authenticate(firstName, lastName, email, password);
        } else {
          getLogger().warn('No email or password provided for authentication after signup', { email, firstName, lastName });
        }

        if (cookieValue) {
          getLogger().info('Removing get started cookie after signup', { cookieValue });
          Cookies.remove(GET_STARTED_COOKIE_NAME);
        }

        trackConversion({ event: 'submit_signup' });
        trackHeapEvent('Store Builder - DIY Signup Complete', { lockerId, variant });
        navigate({
          to: '/builder/$slug',
          params: { slug: String(lockerId) },
          replace: true,
          // Forwards utm_variant so LaunchDarkly's context still carries it once the user lands in
          // the builder — the redirect otherwise drops all UTM params from the URL.
          ...(hasUtmVariantCookie() ? { search: { utm_variant: FAN_FAVORITES_UTM_VARIANT_VALUE } } : {}),
        });

        return teamLockerError;
      } catch (error) {
        return parseValidationError(error);
      }
    },
    [navigate, signup, trackConversion, authenticate],
  );

  return { submitSignup, isPending, authenticate, isAuthenticating };
}
